/**
 * The town's houses (lifted from lantern-water's `prefabs/house-shell.js`
 * `houseShell` and the house helpers of `world.js`: `facadeBody`,
 * `carvedEntrance`, `mansardRoof`, `stoneFacade`, `facadeDetails`, `oriel`,
 * `merchantGable`). One shell builds all six houses; `type` picks the facade:
 *
 * - 0: timber-framed market house (gable roof, balcony, swags, timber frame);
 * - 1: the apothecary (carved entrance, awning, trade sign, carved front);
 * - 2: plaster house with a hip roof and a shallow carved lintel;
 * - 3: the river house (mansard roof, stone facade, bowed balcony, plaque);
 * - 4: the merchants' house (crow-stepped gable, oculus, hoist, banner).
 *
 * Built in `parent`'s space at (x, z) with its front toward +z, sitting on
 * y 0 (the placement lifts it to its terrace). Every `r()` draw of
 * `world.js` comes from the prefab's own stream (`ctx.random`). The walls'
 * weathering is painted into the kit's facade atlas, one cell per house
 * shape, kept for the kit's life (a rebuilt town repaints nothing). Signs
 * are blank boards (`signTexture`: no lettering, ADR 0005). The prototype's
 * colliders, porch surfaces, chimney, lantern light and interactions are the
 * scene record's (ADR 0031); `houseShell` builds geometry only.
 */
import * as THREE from 'three';
import { type FacadeTile, signTexture } from './art.ts';
import { apothecaryExterior } from './apothecary-exterior.ts';
import { paneledDoor } from './door-props.ts';
import type { Ctx, Kit } from './kit.ts';
import { shopCanopy } from './market-props.ts';
import { riverBalcony } from './river-balcony.ts';
import { timberHouse } from './timber-house.ts';

type P = THREE.Object3D;

/** Facade atlas cells painted per kit, by shape: the atlas holds eight, and a rebuilt scene reuses its cell. */
const painted = new WeakMap<Kit, Map<string, FacadeTile>>();

/** A blank signboard's material at the prototype's roughness (one per roughness, shared by the kit). */
const signMaterial = (ctx: Ctx, roughness: number): THREE.MeshStandardMaterial =>
  ctx.kit.shared(`house-shell:sign:${roughness}`, () => new THREE.MeshStandardMaterial({ map: ctx.kit.shared('house-shell:sign-map', signTexture), roughness }));

const facadeBody = (ctx: Ctx, p: P, x: number, z: number, width: number, depth: number, height: number, type: number): void => {
  const key = `${width}:${height}:${type}`;
  let cells = painted.get(ctx.kit);
  if (cells === undefined) {
    cells = new Map();
    painted.set(ctx.kit, cells);
  }
  let tile = cells.get(key);
  if (tile === undefined) {
    tile = ctx.kit.facades.paint(width, height, type);
    cells.set(key, tile);
  }
  const geo = new THREE.BoxGeometry(width, height, depth);
  const uv = geo.getAttribute('uv');
  const colors: number[] = [];
  const color = new THREE.Color(['#e0e3d9', '#eee0cf', '#c7d1db', '#dce1e8', '#e3d0c8'][type] ?? '#e0e3d9');
  for (let i = 0; i < uv.count; i += 1) {
    uv.setXY(i, tile.u + uv.getX(i) * tile.du, tile.v + uv.getY(i) * tile.dv);
    colors.push(color.r, color.g, color.b);
  }
  geo.setAttribute('color', new THREE.Float32BufferAttribute(colors, 3));
  ctx.mesh(p, geo, ctx.kit.facadeMaterial, x, height / 2, z);
};

const carvedEntrance = (ctx: Ctx, p: P, x: number, z: number, type = 1): void => {
  const m = ctx.m;
  const box = (...a: Parameters<Ctx['box']>): ReturnType<Ctx['box']> => ctx.box(...a);
  const mesh = (...a: Parameters<Ctx['mesh']>): ReturnType<Ctx['mesh']> => ctx.mesh(...a);
  const line = (...a: Parameters<Ctx['line']>): ReturnType<Ctx['line']> => ctx.line(...a);
  mesh(p, new THREE.ExtrudeGeometry(ctx.archShape(1.5, 2.48), { depth: 0.18, bevelEnabled: false, curveSegments: 12 }), m.stone, x, 1.27, z + 0.13);
  const joinery = new THREE.Group();
  joinery.position.set(x, 0, z);
  p.add(joinery);
  const tall = type === 4 || type === 1;
  paneledDoor(ctx, joinery, type, true, tall);
  for (const side of [-1, 1]) {
    box(p, x + side * 0.68, 1.03, z + 0.31, 0.18, 1.88, 0.23, m.cream);
    box(p, x + side * 0.68, 0.18, z + 0.36, 0.3, 0.22, 0.33, m.stone);
    box(p, x + side * 0.68, 1.89, z + 0.34, 0.28, 0.14, 0.29, m.cream);
  }
  const fanHeight = tall ? 0.6 : 0.9;
  const fanY = tall ? 1.94 : 1.76;
  const transomY = tall ? 1.6 : 1.37;
  const fanGeometry = new THREE.ShapeGeometry(ctx.archShape(0.89, fanHeight), 10);
  const fanUv = fanGeometry.getAttribute('uv');
  for (let i = 0; i < fanUv.count; i += 1) fanUv.setXY(i, fanUv.getX(i) / 0.89 + 0.5, fanUv.getY(i) / fanHeight + 0.5);
  mesh(p, fanGeometry, m.windowGlass, x, fanY, z + 0.35);
  for (const side of [-1, 1]) line(p, [x, transomY + 0.05, z + 0.38], [x + side * 0.32, tall ? 2.1 : 1.91, z + 0.38], 0.027, m.brass);
  box(p, x, tall ? 1.93 : 1.6, z + 0.39, 0.035, tall ? 0.6 : 0.55, 0.025, m.darkWood);
  box(p, x, transomY, z + 0.4, 0.92, 0.08, 0.07, m.cream);
  // The apothecary's canopy covers the arch: it keeps only the plain surround.
  if (type === 1) return;
  for (let i = 0; i <= 10; i += 1) {
    const angle = (i * Math.PI) / 10;
    const radius = 0.65;
    const stone = box(p, x + Math.cos(angle) * radius, 1.83 + Math.sin(angle) * radius, z + 0.31, 0.19, 0.24, 0.25, m.cream);
    stone.rotation.z = angle - Math.PI / 2;
  }
  box(p, x, 2.51, z + 0.38, 0.24, 0.33, 0.31, m.cream);
};

const mansardRoof = (ctx: Ctx, p: P, x: number, y: number, z: number, width: number, depth: number): void => {
  const m = ctx.m;
  const box = (...a: Parameters<Ctx['box']>): ReturnType<Ctx['box']> => ctx.box(...a);
  const mesh = (...a: Parameters<Ctx['mesh']>): ReturnType<Ctx['mesh']> => ctx.mesh(...a);
  const line = (...a: Parameters<Ctx['line']>): ReturnType<Ctx['line']> => ctx.line(...a);
  const ring = (scale: number, yy: number): (readonly [number, number, number])[] => [
    [(-width / 2) * scale, yy, (-depth / 2) * scale],
    [(width / 2) * scale, yy, (-depth / 2) * scale],
    [(width / 2) * scale, yy, (depth / 2) * scale],
    [(-width / 2) * scale, yy, (depth / 2) * scale],
  ];
  const lower = ring(1, 0);
  const upper = ring(0.67, 1.65);
  const pos: number[] = [];
  const uv: number[] = [];
  const at = (list: readonly (readonly [number, number, number])[], i: number): readonly [number, number, number] => list[i] ?? [0, 0, 0];
  for (let i = 0; i < 4; i += 1) {
    const j = (i + 1) % 4;
    const face = [at(lower, i), at(lower, j), at(upper, j), at(upper, i)];
    const span = i % 2 ? depth : width;
    const li = at(lower, i);
    const lj = at(lower, j);
    const ex = (lj[0] - li[0]) / span;
    const ez = (lj[2] - li[2]) / span;
    const run = (i % 2 ? width : depth) * 0.165;
    for (const k of [0, 3, 1, 1, 3, 2]) {
      const v = face[k] ?? li;
      pos.push(x + v[0], y + v[1], z + v[2]);
      const alongEave = (v[0] - li[0]) * ex + (v[2] - li[2]) * ez;
      const alongSlope = (v[1] / 1.65) * Math.hypot(run, 1.65);
      uv.push(alongEave / 3, alongSlope / 3);
    }
    const ui = at(upper, i);
    line(p, [x + li[0], y, z + li[2]], [x + ui[0], y + 1.65, z + ui[2]], 0.095, m.iron);
  }
  const geo = new THREE.BufferGeometry();
  geo.setAttribute('position', new THREE.Float32BufferAttribute(pos, 3));
  geo.setAttribute('uv', new THREE.Float32BufferAttribute(uv, 2));
  geo.computeVertexNormals();
  mesh(p, geo, m.roofDark, 0, 0, 0);
  ctx.hipRoof(p, x, y + 1.65, z, width * 0.69, depth * 0.69, 0.55, m.roofDark);
  box(p, x, y - 0.04, z, width + 0.2, 0.19, depth + 0.2, m.cream);
  for (const dx of [-width * 0.26, width * 0.26]) {
    box(p, x + dx, y + 0.57, z + depth * 0.34, 1.1, 1.13, 0.9, m.limestone);
    ctx.windowFrame(p, x + dx, y + 0.57, z + depth * 0.34 + 0.47, 0.62, 0.9, false);
    ctx.roof(p, x + dx, y + 1.17, z + depth * 0.34, 1.28, 1.0, 0.49, m.roofDark);
    for (const side of [-1, 1]) box(p, x + dx + side * 0.49, y + 0.58, z + depth * 0.34 + 0.49, 0.12, 1.16, 0.17, m.cream);
  }
  for (let xx = x - width * 0.27; xx < x + width * 0.3; xx += 0.32) {
    box(p, xx, y + 2.39, z, 0.038, 0.37, 0.04, m.iron);
    mesh(p, ctx.sphere, m.brass, xx, y + 2.6, z, 0.043, 0.067, 0.043);
  }
  line(p, [x - width * 0.3, y + 2.48, z], [x + width * 0.3, y + 2.48, z], 0.038, m.iron);
};

const stoneFacade = (ctx: Ctx, p: P, x: number, z: number, width: number, depth: number, height: number): void => {
  const m = ctx.m;
  const box = (...a: Parameters<Ctx['box']>): ReturnType<Ctx['box']> => ctx.box(...a);
  const mesh = (...a: Parameters<Ctx['mesh']>): ReturnType<Ctx['mesh']> => ctx.mesh(...a);
  const line = (...a: Parameters<Ctx['line']>): ReturnType<Ctx['line']> => ctx.line(...a);
  const f = z + depth / 2;
  for (const dx of [-width * 0.48, -width * 0.14, width * 0.14, width * 0.48]) {
    box(p, x + dx, height / 2, f + 0.13, 0.19, height, 0.27, m.cream);
    for (const yy of [0.25, 2.5, height - 0.18]) {
      box(p, x + dx, yy, f + 0.2, 0.35, 0.14, 0.34, m.stone);
      box(p, x + dx, yy + 0.12, f + 0.21, 0.28, 0.1, 0.31, m.cream);
    }
  }
  for (const yy of [2.54, 4.3, height]) {
    box(p, x, yy, f + 0.17, width + 0.28, 0.17, 0.36, m.cream);
    box(p, x, yy + 0.14, f + 0.14, width + 0.12, 0.07, 0.27, m.stone);
  }
  for (let dx = -width * 0.47; dx < width * 0.48; dx += 0.24) box(p, x + dx, height - 0.18, f + 0.19, 0.11, 0.17, 0.29, m.cream);
  for (const dx of [-width * 0.29, width * 0.29]) {
    ctx.windowFrame(p, x + dx, 3.3, f + 0.16, 0.85, 1.22, false);
    for (const yy of [3.3, height * 0.73]) {
      const h = 0.72;
      line(p, [x + dx - h, yy + 0.69, f + 0.21], [x + dx, yy + 1.02, f + 0.21], 0.095, m.cream);
      line(p, [x + dx, yy + 1.02, f + 0.21], [x + dx + h, yy + 0.69, f + 0.21], 0.095, m.cream);
      box(p, x + dx, yy - 0.71, f + 0.39, 1.37, 0.13, 0.67, m.cream);
      for (let i = 0; i < 6; i += 1) box(p, x + dx - 0.55 + i * 0.22, yy - 0.39, f + 0.72, 0.035, 0.55, 0.04, m.iron);
      box(p, x + dx, yy - 0.1, f + 0.72, 1.27, 0.05, 0.065, m.iron);
    }
  }
  const shield = new THREE.Shape();
  shield.moveTo(0, 0.65);
  shield.lineTo(0.55, 0.47);
  shield.lineTo(0.44, -0.25);
  shield.quadraticCurveTo(0.27, -0.51, 0, -0.66);
  shield.quadraticCurveTo(-0.27, -0.51, -0.44, -0.25);
  shield.lineTo(-0.55, 0.47);
  shield.closePath();
  mesh(p, new THREE.ExtrudeGeometry(shield, { depth: 0.1, bevelEnabled: false }), m.cream, x, height * 0.77, f + 0.18);
  mesh(p, new THREE.ShapeGeometry(shield, 12), m.teal, x, height * 0.77, f + 0.285, 0.83, 0.83, 1);
  for (let i = 0; i < 3; i += 1) {
    const yy = height * 0.77 + 0.14 - i * 0.18;
    const curve = new THREE.CatmullRomCurve3([new THREE.Vector3(x - 0.31, yy, f + 0.3), new THREE.Vector3(x - 0.1, yy + 0.06, f + 0.3), new THREE.Vector3(x + 0.13, yy - 0.04, f + 0.3), new THREE.Vector3(x + 0.31, yy + 0.03, f + 0.3)]);
    mesh(p, new THREE.TubeGeometry(curve, 12, 0.019, 4, false), m.brass, 0, 0, 0);
  }
  ctx.windowFrame(p, x, 3.35, f + 0.16, 0.92, 1.42, false);
  riverBalcony(ctx, p, x, f);
  const board = mesh(p, ctx.plane, signMaterial(ctx, 1), x, 3.08, f + 0.98, 1.32, 0.55, 1);
  board.castShadow = false;
};

const facadeDetails = (ctx: Ctx, p: P, x: number, z: number, width: number, depth: number, height: number, type: number): void => {
  const m = ctx.m;
  const box = (...a: Parameters<Ctx['box']>): ReturnType<Ctx['box']> => ctx.box(...a);
  const mesh = (...a: Parameters<Ctx['mesh']>): ReturnType<Ctx['mesh']> => ctx.mesh(...a);
  const line = (...a: Parameters<Ctx['line']>): ReturnType<Ctx['line']> => ctx.line(...a);
  const cylinder = (...a: Parameters<Ctx['cylinder']>): ReturnType<Ctx['cylinder']> => ctx.cylinder(...a);
  const r = ctx.random;
  const front = z + depth / 2;
  const side = new THREE.Group();
  side.position.set(x + width / 2 + 0.02, 0, z);
  side.rotation.y = Math.PI / 2;
  p.add(side);
  for (const xx of [-depth * 0.24, depth * 0.24]) {
    ctx.windowFrame(side, xx, height * 0.72, 0, 0.62, 1.08, false, m.windowCool);
    ctx.windowFrame(side, xx, 1.38, 0, 0.65, 1.14, false);
  }
  if (type !== 2 && type !== 3 && type !== 4) {
    line(p, [x + width / 2 + 0.1, height, z - depth / 2], [x + width / 2 + 0.1, height + 1.7, z], 0.11);
    line(p, [x + width / 2 + 0.1, height, z + depth / 2], [x + width / 2 + 0.1, height + 1.7, z], 0.11);
    box(p, x + width / 2 + 0.12, height + 0.7, z, 0.12, 1.4, 0.12, m.darkWood);
  }
  if (type === 0 || type === 4) {
    const by = height * 0.52;
    box(p, x, by, front + 0.42, width * 0.84, 0.16, 1.05, m.wood);
    box(p, x, by + 1.03, front + 0.97, width * 0.88, 0.075, 0.075, m.iron);
    box(p, x, by + 0.18, front + 0.97, width * 0.86, 0.06, 0.06, m.iron);
    for (let xx = -width * 0.4; xx <= width * 0.41; xx += 0.27) {
      box(p, x + xx, by + 0.61, front + 0.97, 0.036, 0.84, 0.036, m.iron);
      mesh(p, ctx.sphere, m.brass, x + xx, by + 0.95, front + 0.97, 0.047, 0.047, 0.047);
    }
    for (const sx of [-1, 1]) {
      line(p, [x + sx * width * 0.4, by, front + 0.8], [x + sx * width * 0.4, by - 0.6, front + 0.06], 0.11, m.darkWood);
      for (const y of [by + 1.03, by + 0.18]) line(p, [x + sx * width * 0.42, y, front + 0.1], [x + sx * width * 0.42, y, front + 0.97], 0.06, m.iron);
    }
    const scroll = new THREE.TorusGeometry(0.115, 0.012, 3, 12, Math.PI * 1.65);
    for (let dx = -width * 0.33; dx < width * 0.35; dx += 0.54) {
      for (const s of [-1, 1]) {
        const o = mesh(p, scroll, m.iron, x + dx + s * 0.09, by + 0.61, front + 0.985);
        o.rotation.z = s * 0.7;
      }
    }
    for (const dx of [-width * 0.29, width * 0.29]) {
      const swag = new THREE.Shape();
      swag.moveTo(-0.8, 0);
      swag.lineTo(0.8, 0);
      for (let j = 12; j >= 0; j -= 1) {
        const t = j / 12;
        swag.lineTo(-0.8 + t * 1.6, -0.1 - Math.sin(t * Math.PI) * 0.25);
      }
      swag.closePath();
      mesh(p, new THREE.ExtrudeGeometry(swag, { depth: 0.055, bevelEnabled: false }), m.red, x + dx, height - 0.15, front + 0.17);
      for (const s of [-1, 1]) mesh(p, ctx.sphere, m.brass, x + dx + s * 0.78, height - 0.22, front + 0.26, 0.045, 0.07, 0.045);
    }
  } else if (type === 2) {
    box(p, x, 2.8, front + 0.16, 1.5, 0.13, 0.35, m.cream);
    for (let i = 0; i < 5; i += 1) box(p, x - 0.6 + i * 0.3, 2.65, front + 0.12, 0.08, 0.24, 0.15, m.cream);
  }
  cylinder(p, x - width * 0.47, height * 0.49, front + 0.19, 0.035, height * 0.97, m.iron);
  for (let i = 0; i < 8; i += 1) box(p, x - width * 0.47, 0.35 + (i * height) / 8, front + 0.19, 0.12, 0.03, 0.1, m.iron);
  for (let i = 0; i < 15; i += 1) {
    const xx = x - width * 0.48 + r() * width * 0.96;
    box(p, xx, 0.43 + r() * 0.22, front + 0.15, 0.18 + r() * 0.2, 0.08 + r() * 0.1, 0.025, m.plaster);
  }
};

/** The eastern house's projecting, chamfered upper bay with its planter, at `front` and `centerY`. */
export const oriel = (ctx: Ctx, p: P, x: number, front: number, centerY: number): void => {
  const m = ctx.m;
  const box = (...a: Parameters<Ctx['box']>): ReturnType<Ctx['box']> => ctx.box(...a);
  const line = (...a: Parameters<Ctx['line']>): ReturnType<Ctx['line']> => ctx.line(...a);
  const footprint: readonly (readonly [number, number])[] = [
    [-1.18, 0],
    [-1.18, 0.28],
    [-0.78, 0.86],
    [0.78, 0.86],
    [1.18, 0.28],
    [1.18, 0],
  ];
  for (let i = 1; i < 4; i += 1) {
    const [ax, az] = footprint[i] ?? [0, 0];
    const [bx, bz] = footprint[i + 1] ?? [0, 0];
    const segment = new THREE.Group();
    segment.position.set(x + (ax + bx) / 2, centerY, front + (az + bz) / 2);
    segment.rotation.y = -Math.atan2(bz - az, bx - ax);
    p.add(segment);
    const length = Math.hypot(bx - ax, bz - az);
    box(segment, 0, 0, 0, length, 1.65, 0.14, m.teal);
    box(segment, 0, -0.68, 0.1, length + 0.1, 0.18, 0.26, m.wood);
    box(segment, 0, 0.77, 0.08, length + 0.08, 0.12, 0.22, m.cream);
    ctx.windowFrame(segment, 0, 0.08, 0.09, length * 0.69, 1.08, false, m.windowCool);
    for (const side of [-1, 1]) box(segment, side * (length / 2 - 0.03), 0, 0.12, 0.075, 1.58, 0.12, m.cream);
  }
  box(p, x, centerY - 0.86, front + 0.35, 2.42, 0.16, 0.9, m.wood);
  for (const side of [-1, 1]) {
    line(p, [x + side * 0.82, centerY - 1.5, front + 0.03], [x + side * 0.82, centerY - 0.91, front + 0.76], 0.15, m.darkWood);
    box(p, x + side * 0.82, centerY - 1.14, front + 0.04, 0.23, 0.72, 0.16, m.cream);
  }
  ctx.hipRoof(p, x, centerY + 0.89, front + 0.36, 2.7, 1.32, 0.57, m.roofDark);
  box(p, x, centerY - 0.54, front + 1.03, 1.34, 0.18, 0.3, m.wood);
  ctx.flowers(p, x, centerY - 0.44, front + 1.04, 1.1, 13);
};

const merchantGable = (ctx: Ctx, p: P, x: number, z: number, width: number, depth: number, height: number): void => {
  const m = ctx.m;
  const box = (...a: Parameters<Ctx['box']>): ReturnType<Ctx['box']> => ctx.box(...a);
  const mesh = (...a: Parameters<Ctx['mesh']>): ReturnType<Ctx['mesh']> => ctx.mesh(...a);
  const line = (...a: Parameters<Ctx['line']>): ReturnType<Ctx['line']> => ctx.line(...a);
  const cylinder = (...a: Parameters<Ctx['cylinder']>): ReturnType<Ctx['cylinder']> => ctx.cylinder(...a);
  const front = z + depth / 2;
  const roofGroup = new THREE.Group();
  roofGroup.position.set(x, height, z);
  roofGroup.rotation.y = Math.PI / 2;
  p.add(roofGroup);
  ctx.roof(roofGroup, 0, 0, 0, depth - 0.6, width, 2.25, m.roofDark);
  for (let tier = 0; tier < 6; tier += 1) {
    const span = width + 0.22 - tier * 0.86;
    const y = height + tier * 0.43 + 0.215;
    box(p, x, y, front + 0.09, span, 0.43, 0.34, m.limestone);
    if (tier === 5) box(p, x, y + 0.23, front + 0.13, span + 0.15, 0.09, 0.46, m.cream);
    else for (const side of [-1, 1]) box(p, x + side * (span / 2 - 0.21), y + 0.23, front + 0.13, 0.58, 0.09, 0.46, m.cream);
    for (const side of [-1, 1]) box(p, x + side * (span / 2 - 0.09), y, front + 0.29, 0.15, 0.36, 0.13, m.stone);
  }
  const oculusY = height + 1.06;
  mesh(p, new THREE.CircleGeometry(0.49, 24), m.dark, x, oculusY, front + 0.29);
  mesh(p, new THREE.CircleGeometry(0.37, 24), m.windowCool, x, oculusY, front + 0.31);
  mesh(p, new THREE.TorusGeometry(0.44, 0.085, 4, 24), m.cream, x, oculusY, front + 0.32);
  for (const angle of [0, Math.PI / 2]) {
    const bar = box(p, x, oculusY, front + 0.34, 0.74, 0.045, 0.06, m.iron);
    bar.rotation.z = angle;
  }
  cylinder(p, x, height + 2.95, front + 0.1, 0.045, 0.55, m.brass);
  mesh(p, ctx.sphere, m.brass, x, height + 3.12, front + 0.1, 0.09, 0.12, 0.09);
  box(p, x, height + 2.15, front + 0.43, 0.11, 0.13, 1.03, m.darkWood);
  line(p, [x, height + 1.86, front + 0.15], [x, height + 2.14, front + 0.78], 0.07, m.iron);
  const hook = mesh(p, new THREE.TorusGeometry(0.1, 0.023, 4, 12, Math.PI * 1.7), m.iron, x, height + 1.98, front + 0.93);
  hook.rotation.z = Math.PI * 0.35;
  for (const side of [-1, 1]) {
    const xx = x + side * (width / 2 - 0.08);
    box(p, xx, height / 2, front + 0.14, 0.26, height, 0.27, m.limestone);
    for (let y = 0.65; y < height; y += 0.48) box(p, xx, y, front + 0.21, 0.42, 0.21, 0.25, m.cream);
  }
  box(p, x, height - 0.12, front + 0.23, width + 0.2, 0.19, 0.38, m.cream);
  for (let dx = -width / 2 + 0.2; dx < width / 2; dx += 0.3) box(p, x + dx, height - 0.3, front + 0.23, 0.11, 0.22, 0.2, m.stone);
  const sill = height * 0.52 + 0.08;
  const doorHeight = 2.06;
  const doorY = sill + doorHeight / 2;
  const doorZ = front + 0.15;
  ctx.windowFrame(p, x, doorY, doorZ, 0.8, doorHeight, false, m.windowCool);
  box(p, x, sill + 0.29, doorZ + 0.19, 0.8, 0.58, 0.05, m.darkWood);
  for (const side of [-1, 1]) {
    box(p, x + side * 0.2, sill + 0.29, doorZ + 0.22, 0.25, 0.4, 0.03, m.wood);
    box(p, x + side * 0.2, sill + 1.2, doorZ + 0.19, 0.36, 0.035, 0.05, m.darkWood);
    box(p, x + side * 0.07, sill + 1.0, doorZ + 0.22, 0.03, 0.14, 0.04, m.brass);
  }
  const bannerTop = height * 0.52 + 0.14;
  const bannerZ = front + 1.02;
  const rod = cylinder(p, x, bannerTop, bannerZ, 0.025, 1.5, m.brass);
  rod.rotation.z = Math.PI / 2;
  for (const side of [-1, 1]) {
    mesh(p, ctx.sphere, m.brass, x + side * 0.76, bannerTop, bannerZ, 0.045, 0.045, 0.045);
    line(p, [x + side * 0.55, bannerTop, bannerZ], [x + side * 0.55, bannerTop + 0.06, front + 0.97], 0.02, m.iron);
  }
  const sign = mesh(p, ctx.plane, signMaterial(ctx, 0.9), x, bannerTop - 0.34, bannerZ + 0.01, 1.3, 0.65, 1);
  sign.castShadow = false;
};

export type HouseDecorations = { readonly ivy: boolean; readonly windowBoxes: boolean };

/** A house of `type` (see the module comment), `width` × `depth`, walls `height` high, at (x, z) in `parent`'s space. */
export const houseShell = (ctx: Ctx, p: P, x: number, z: number, width: number, depth: number, height: number, type: number, decorations: HouseDecorations): void => {
  const m = ctx.m;
  const box = (...a: Parameters<Ctx['box']>): ReturnType<Ctx['box']> => ctx.box(...a);
  const mesh = (...a: Parameters<Ctx['mesh']>): ReturnType<Ctx['mesh']> => ctx.mesh(...a);
  const r = ctx.random;
  const trim = type === 3 || type === 4 ? m.cream : m.darkWood;
  facadeBody(ctx, p, x, z, width, depth, height, type);
  box(p, x, 0.35, z, width + 0.2, 0.7, depth + 0.2);
  for (const y of [1, height * 0.54, height]) {
    box(p, x, y, z + depth / 2 + 0.04, width + 0.13, 0.13, 0.13, trim);
    box(p, x - width / 2 - 0.04, y, z, 0.13, 0.13, depth, m.darkWood);
    box(p, x + width / 2 + 0.04, y, z, 0.13, 0.13, depth, m.darkWood);
  }
  for (const xx of [x - width / 2 + 0.07, x + width / 2 - 0.07]) box(p, xx, height / 2, z + depth / 2 + 0.08, 0.15, height, 0.15, trim);
  const f = z + depth / 2;
  for (const dx of [-width * 0.29, width * 0.29]) {
    ctx.windowFrame(p, x + dx, height * 0.73, f + 0.07, 0.76, 1.02, true, type === 1 ? m.windowGlass : m.windowCool);
    ctx.windowFrame(p, x + dx, 1.42, f + 0.07, 0.8, 1.13);
  }
  if (type === 1 || type === 3 || type === 4) carvedEntrance(ctx, p, x, f, type);
  else {
    box(p, x, 1.08, f + 0.09, 1.12, 2.16, 0.18, m.stone);
    const joinery = new THREE.Group();
    joinery.position.set(x, 0, f);
    p.add(joinery);
    paneledDoor(ctx, joinery, type, false);
    box(p, x, 1.61, f + 0.285, 0.58, 0.46, 0.035, m.windowCool);
  }
  box(p, x, 0.28, f + 0.7, 1.5, 0.22, 1);
  box(p, x, 0.12, f + 1.15, 1.75, 0.18, 0.65);
  if (type === 4) merchantGable(ctx, p, x, z, width, depth, height);
  else if (type === 3) mansardRoof(ctx, p, x, height, z, width + 0.55, depth + 0.5);
  else if (type === 2) ctx.hipRoof(p, x, height, z, width + 0.5, depth + 0.45, 1.85, m.roofDark);
  else ctx.roof(p, x, height, z, width, depth, 1.9, m.roof);
  facadeDetails(ctx, p, x, z, width, depth, height, type);
  if (type === 0) timberHouse(ctx, p, x, z, width, depth, height);
  if (type === 3) stoneFacade(ctx, p, x, z, width, depth, height);
  box(p, x + width * 0.3, height + 1.5, z - 0.5, 0.65, 2.1, 0.65);
  // An open coping ring over a dark flue: a working chimney, not a capped block.
  const cx = x + width * 0.3;
  const cz = z - 0.5;
  box(p, cx, height + 2.56, cz, 0.5, 0.02, 0.48, m.dark);
  for (const side of [-1, 1]) {
    box(p, cx + side * 0.34, height + 2.6, cz, 0.17, 0.18, 0.83, m.cream);
    box(p, cx, height + 2.6, cz + side * 0.33, 0.51, 0.18, 0.17, m.cream);
  }
  if (type !== 3 && type !== 4) {
    box(p, x - 0.65, height + 0.7, z + depth * 0.28, 1.4, 1.15, 0.8, m.plaster);
    ctx.roof(p, x - 0.65, height + 1.27, z + depth * 0.28, 1.5, 1.1, 0.55);
    ctx.windowFrame(p, x - 0.65, height + 0.75, z + depth * 0.28 + 0.44, 0.55, 0.64, false);
  }
  for (let a = 0; a < 4; a += 1) {
    box(p, x - width / 2 - 0.06, 0.65 + a * 0.8, z + depth / 2 + 0.04, 0.27, 0.22, 0.28);
    box(p, x + width / 2 + 0.06, 0.65 + a * 0.8, z + depth / 2 + 0.04, 0.27, 0.22, 0.28);
  }
  // Ivy: its random draws are taken whether or not it is shown, so the rest of the house stays put.
  const ivy = new THREE.Group();
  ivy.name = 'ivy';
  for (let i = 0; i < 15; i += 1) {
    const xx = x - width / 2 + 0.12 + (i % 3) * 0.12;
    const yy = 0.55 + i * 0.13;
    const o = mesh(ivy, ctx.plane, ctx.leafMats[0], xx, yy, f + 0.2, 0.47, 0.55, 1);
    o.rotation.z = (r() - 0.5) * 0.5;
  }
  if (decorations.ivy) p.add(ivy);
  ctx.lantern(p, x + width * 0.47, 2.15, f + 0.48);
  if (decorations.windowBoxes) {
    for (const dx of [-width * 0.29, width * 0.29]) {
      box(p, x + dx, height * 0.73 - 0.7, f + 0.34, 1.2, 0.24, 0.4, m.wood);
      ctx.flowers(p, x + dx, height * 0.73 - 0.55, f + 0.33, 0.8, 10);
    }
  }
  if (type === 1) {
    const sign = mesh(p, ctx.plane, signMaterial(ctx, 0.7), x, 2.8, f + 0.24, 1.85, 0.92, 1);
    sign.castShadow = false;
    shopCanopy(ctx, p, x, 2.47, f + 0.22, 3.12, 1.3);
    apothecaryExterior(ctx, p, x, f, width, height);
  }
};
